import { RelativeTimeFormatSingularUnit } from '@formatjs/ecma402-abstract';
import {
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInWeeks,
    differenceInYears,
} from 'date-fns';

import { getToday } from './date';

/**
 * Returns the elapsed time between two dates
 * @param from - The date to start the calculation from
 * @param to - The date to calculate the elapsed time to
 * @returns The elapsed time as a positive value and a unit (minute or hour)
 */
export function getElapsedMinOrHourSince(
    from: Date,
    to: Date,
): { value: number; unit: 'minute' | 'hour' } {
    const minutes = Math.abs(differenceInMinutes(to, from));
    if (minutes < 60) {
        return { value: minutes, unit: 'minute' };
    }
    const hours = Math.abs(differenceInHours(to, from));
    return { value: hours, unit: 'hour' };
}

/**
 * Returns the elapsed time between two dates
 * @param date - The date to calculate the elapsed time to
 * @returns The elapsed time as a value and a unit (year, month, week, day, hour, minute)
 */
export const bestFitUnitDateDifference: (date: Date | null) => {
    value: number;
    unit: RelativeTimeFormatSingularUnit;
} = (date: Date | null) => {
    if (date) {
        const currentDate = getToday();

        const diffYears = differenceInYears(currentDate, date);
        if (diffYears > 0) {
            return {
                value: -diffYears,
                unit: 'year',
            };
        }

        const diffMonths = differenceInMonths(currentDate, date);
        if (diffMonths > 0) {
            return {
                value: -diffMonths,
                unit: 'month',
            };
        }

        const diffWeeks = differenceInWeeks(currentDate, date);
        if (diffWeeks > 0) {
            return {
                value: -diffWeeks,
                unit: 'week',
            };
        }

        const diffDays = differenceInDays(currentDate, date);
        if (diffDays > 0) {
            return {
                value: -diffDays,
                unit: 'day',
            };
        }

        const diffHours = differenceInHours(currentDate, date);
        if (diffHours > 0) {
            return {
                value: -diffHours,
                unit: 'hour',
            };
        }

        const diffMinutes = differenceInMinutes(currentDate, date);
        if (diffMinutes > 0) {
            return {
                value: -diffMinutes,
                unit: 'minute',
            };
        }
    }
    return {
        value: -1,
        unit: 'minute',
    };
};
